/* eslint-disable import/no-anonymous-default-export */
/**
 * 제휴 카페 캠페인 관리 설정
 */
import RefundBadge from "../components/RefundBadge";

function getStatusLabel(status) {
  switch (status) {
    case "waiting":
      return "대기중";
    case "active":
      return "활성화";
    case "expiringSoon":
      return "종료임박";
    case "terminated":
      return "종료";
    default:
      return status;
  }
}

/**
 * 총금액은 부가세 포함(결제 기준 totalAmount), 단가는 부가세 별도 — 블로그·맘카페 등 다른 상품과 같은 기준 (AMP-2591)
 * totalPrice는 앱이 보낸 부가세 별도 금액(광고비 + 언더키워드 비용)이다.
 */
export const getTotalWithVat = (row) => row.totalAmount ?? row.totalPrice ?? 0;
export const getUnitPrice = (row) =>
  row.totalQuantity ? Math.round((row.totalPrice ?? 0) / row.totalQuantity) : 0;

export default {
  id: "adPartnerCafe",
  title: "[제휴 카페]신청 관리",

  api: {
    list: {
      method: "GET",
      url: "/advertisement/admin/campaigns/by-type",
      params: { type: "partner_cafe" },
    },
    count: {
      method: "GET",
      url: "/advertisement/admin/campaigns/by-type/count",
      params: { type: "partner_cafe" },
    },
    submitted: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    paid: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    complete: {
      method: "POST",
      url: "/advertisement/admin/campaigns/by-type/status",
    },
    refund: {
      method: "POST",
      url: "/advertisement/admin/campaigns/refund",
      version: 2,
    },
    delete: {
      method: "DELETE",
      url: "/advertisement/admin/campaigns/by-type",
    },
  },

  filters: {
    default: {
      type: ["partner_cafe"],
      keyword: "",
      submitDate: "",
      status: "전체",
      submitted: "전체",
      paid: "전체",
      page: 1,
      limit: 50,
    },
    schema: [
      {
        name: "type",
        label: "상품 타입",
        type: "checkbox-group",
        options: [{ value: "partner_cafe", label: "제휴 카페 배포" }],
      },
      {
        name: "keyword",
        label: "검색",
        type: "text",
        placeholder: "신청자, 플레이스명",
      },
      {
        name: "submitDate",
        label: "접수일",
        type: "date",
      },
      {
        name: "submitted",
        label: "접수여부",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "접수완료" },
          { value: "notComplete", label: "미접수" },
        ],
      },
      {
        name: "paid",
        label: "결제상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "결제완료" },
          { value: "notComplete", label: "미결제" },
          { value: "refund", label: "환불" },
        ],
      },
      {
        name: "status",
        label: "상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "waiting", label: "대기중" },
          { value: "active", label: "활성화" },
          { value: "expiringSoon", label: "종료임박" },
          { value: "terminated", label: "종료" },
        ],
      },
    ],
  },

  summary: {
    fields: [
      { key: "totalCount", label: "총 신청" },
      { key: "waitingCount", label: "대기중" },
      { key: "activeCount", label: "활성화" },
      { key: "expiringSoonCount", label: "종료임박" },
      { key: "terminatedCount", label: "종료" },
    ],
  },

  table: {
    keyField: "adPartnerCafeId",
    columns: [
      {
        key: "adCampaignMetaId",
        label: "아이디",
        center: true,
      },
      {
        key: "createdAt",
        label: "캐랭접수시간",
        center: true,
        render: (row) => {
          if (!row.createdAt) return "";
          const [date, time] = row.createdAt.split(" ");
          return (
            <>
              <div>{date}</div>
              <div>{time?.slice(0, 8)}</div>
            </>
          );
        },
      },
      {
        key: "userName",
        label: "신청자(ID)\n상태",
        center: true,
        render: (row) => (
          <>
            <div>
              <span className="user-name">
                {row.userName}
                {row.userId ? ` (${row.userId})` : ""}
              </span>
            </div>
            <div>
              <span className={`badge badge-${row.status}`}>
                {getStatusLabel(row.status)}
              </span>
            </div>
          </>
        ),
      },
      {
        key: "submitDate",
        label: "접수일",
        center: true,
        render: (row) => (row.createdAt ? row.createdAt.split(" ")[0] : "-"),
      },
      {
        key: "productInfo",
        label: "상품타입\n총수량\n총금액(단가)",
        center: true,
        render: (row) => (
          <>
            <div>제휴 카페 배포</div>
            <div>{row.totalQuantity?.toLocaleString() || 0}건</div>
            <div>
              {getTotalWithVat(row).toLocaleString()}원 ({getUnitPrice(row).toLocaleString()}원)
            </div>
          </>
        ),
      },
      {
        key: "placeInfo",
        label: "플레이스명\n연락처\n플레이스URL",
        center: true,
        render: (row) => (
          <>
            <div>{row.placeName || "-"}</div>
            <div>{row.phoneNum || "-"}</div>
            <div>
              {row.placeUrl ? (
                <a
                  href={row.placeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.placeUrl}
                </a>
              ) : (
                "-"
              )}
            </div>
          </>
        ),
      },
      {
        key: "cafeKeywords",
        label: "언더키워드\n선택 카페\n강조키워드",
        center: true,
        render: (row) => (
          <>
            <div>{row.isUnderKeyword ? "언더키워드" : "-"}</div>
            <div>
              {Array.isArray(row.selectedCafes) && row.selectedCafes.length > 0
                ? row.selectedCafes.join(", ")
                : "-"}
            </div>
            <div>{row.emphasizedKeywords || "-"}</div>
          </>
        ),
      },
      {
        key: "guideInfo",
        label: "가이드라인\n사진설명(선택)",
        center: true,
        render: (row) => (
          <>
            <div>{row.guideline || "-"}</div>
            <div>{row.photoDescriptionText || "-"}</div>
          </>
        ),
      },
      {
        key: "paymentType",
        label: "결제방법\n결제상태",
        center: true,
        render: (row) => (
          <>
            <div>
              {row.paymentType === "card"
                ? "카드"
                : row.paymentType === "v_bank"
                  ? "가상계좌"
                  : row.paymentType === "account"
                    ? "계좌이체"
                    : row.paymentType === "point"
                      ? "포인트 결제"
                      : row.paymentType
                        ? row.paymentType
                        : "-"}
            </div>
            <div>
              {row.isRefunded === 1 ? (
                <RefundBadge
                  refundedAmount={row.refundedAmount ?? 0}
                  remainingAmount={row.remainingAmount ?? 0}
                  refundDate={row.refundDate}
                />
              ) : (
                <span
                  className={`badge badge-${
                    row.isPaid === 1 ? "결제완료" : "미결제"
                  }`}
                >
                  {row.isPaid === 1 ? "결제완료" : "미결제"}
                </span>
              )}
            </div>
          </>
        ),
      },
      {
        key: "report",
        label: "리포트",
        center: true,
        render: (row, handlers) => (
          <button
            type="button"
            className="status-btn submitted"
            onClick={(e) => {
              e.stopPropagation();
              handlers?.openReportModal?.(row.adCampaignMetaId, row.placeName);
            }}
          >
            리포트
          </button>
        ),
      },
      {
        key: "notification",
        label: "알림톡",
        center: true,
        render: (row) => {
          const productName = "제휴 카페 배포";

          return (
            <button
              type="button"
              className="notification-btn"
              data-campaign-meta-id={row.adCampaignMetaId}
              data-action="notification"
              data-user-id={row.userId}
              data-product-name={productName}
              data-submitted-at={row.createdAt || ""}
              data-purchase-amount={row.totalCost ?? ""}
              onClick={(e) => e.stopPropagation()}
            >
              알림톡
            </button>
          );
        },
      },
      {
        key: "memo",
        label: "메모",
        center: true,
        render: (row) => (
          <button
            type="button"
            className={row.memo ? "memo-btn has-memo" : "memo-btn"}
            data-action="memo"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-memo={row.memo || ""}
            onClick={(e) => e.stopPropagation()}
          >
            {row.memo ? (
              <span className="memo-preview">{row.memo}</span>
            ) : (
              "메모"
            )}
          </button>
        ),
      },
    ],
  },

  actions: [
    {
      id: "submitted",
      label: "접수\n취소",
      actionType: "submitted",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const selectedItems = items.filter((item) =>
          selectedIds.includes(item.adPartnerCafeId),
        );
        const adCampaignMetaIds = [
          ...new Set(
            selectedItems
              .map((item) => item.adCampaignMetaId)
              .filter((id) => id),
          ),
        ];
        const hasUnsubmitted = selectedItems.some(
          (item) => item.isSubmitted !== 1,
        );
        return {
          type: "partner_cafe",
          action: "submitted",
          ids: adCampaignMetaIds,
          toggleValue: hasUnsubmitted ? 1 : 0,
        };
      },
    },
    {
      id: "paid",
      label: "결제완료",
      actionType: "paid",
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        const alreadyPaid = items.some(
          (item) =>
            selectedIds.includes(item.adPartnerCafeId) && item.isPaid === 1,
        );
        return !alreadyPaid;
      },
      payloadBuilder: (selectedIds, items) => {
        const selectedItems = items.filter((item) =>
          selectedIds.includes(item.adPartnerCafeId),
        );
        const adPaymentTransactionIds = [
          ...new Set(
            selectedItems
              .map((item) => item.adPaymentTransactionId)
              .filter((id) => id),
          ),
        ];
        return {
          type: "partner_cafe",
          action: "paid",
          ids: adPaymentTransactionIds,
          toggleValue: 1,
        };
      },
    },
    {
      id: "complete",
      label: "처리완료\n취소",
      actionType: "complete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const selectedItems = items.filter((item) =>
          selectedIds.includes(item.adPartnerCafeId),
        );
        const hasUncompleted = selectedItems.some(
          (item) => item.isTerminated !== 1,
        );
        return {
          type: "partner_cafe",
          action: "completed",
          ids: selectedIds,
          toggleValue: hasUncompleted ? 1 : 0,
        };
      },
    },
    {
      id: "refund",
      label: "환불",
      actionType: "refund",
      requires: "ad.campaign.refund", // 권한 없으면 버튼 숨김 (R-FE-04)
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        return items.some(
          (item) =>
            selectedIds.includes(item.adPartnerCafeId) &&
            (item.isPaid === 1 || item.isPaid === 2) &&
            (item.remainingAmount ?? 0) > 0,
        );
      },
    },
    {
      id: "delete",
      label: "삭제",
      actionType: "delete",
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        const selected = items.filter((i) =>
          selectedIds.includes(i.adPartnerCafeId),
        );
        return !selected.some((c) => {
          const name = c.userName || "";
          return ![
            "테스트",
            "전유상",
            "이령빈",
            "서한석",
            "null",
            "채영호",
            "매니저",
          ].some((n) => name.includes(n));
        });
      },
      confirm: "선택한 제휴 카페 캠페인을 삭제하시겠습니까?",
      payloadBuilder: (selectedIds, items) => {
        const ids = items
          .filter((i) => selectedIds.includes(i.adPartnerCafeId))
          .map((i) => i.adPartnerCafeId);
        return { type: "partner_cafe", ids };
      },
    },
  ],

  download: {
    type: "xlsx",
    fileName: "제휴카페_신청목록.xlsx",
    sheetName: "제휴카페신청목록",
    transformRow: (row) => ({
      신청자: row.userName || "",
      접수일: row.createdAt ? row.createdAt.split(" ")[0] : "",
      상품타입: "제휴 카페 배포",
      총수량: row.totalQuantity || 0,
      총금액: getTotalWithVat(row),
      단가: getUnitPrice(row),
      플레이스명: row.placeName || "",
      연락처: row.phoneNum || "",
      플레이스URL: row.placeUrl || "",
      언더키워드: row.isUnderKeyword ? "Y" : "N",
      "선택 카페": Array.isArray(row.selectedCafes)
        ? row.selectedCafes.join(", ")
        : row.selectedCafesText || "",
      강조키워드: row.emphasizedKeywords || "",
      가이드라인: row.guideline || "",
      사진설명: row.photoDescriptionText || "",
    }),
  },
};
